import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Helmet } from 'react-helmet'
import './Home.scss'
import { getDprImageUrl } from '../utils/imageLoader'
const Home = ({ images = [] }) => {
    const [hoveredItem, setHoveredItem] = useState(null)
    const [columnAssignments, setColumnAssignments] = useState([]) // [{ col: 0|1|2, image, width, span }]
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
    const [imageDimensions, setImageDimensions] = useState({})
    const [firstImageVisible, setFirstImageVisible] = useState(false)
    const [imagesPreloaded, setImagesPreloaded] = useState(false)
    const staggerTimeoutsRef = useRef([])
    const preloadedImagesRef = useRef(new Set())
    const previousImagesRef = useRef(new Set()) // Track previously shown images

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // Preload all images when component mounts
    useEffect(() => {
        if (images.length === 0) return

        const preloadAllImages = async () => {
            const imageUrls = images.map((img) => getDprImageUrl(img.asset || img.src, 750))

            const preloadPromises = imageUrls.map((src, idx) => {
                return new Promise((resolve) => {
                    const img = new Image()
                    img.onload = () => {
                        preloadedImagesRef.current.add(src)
                        // Capture natural dimensions
                        setImageDimensions(prev => ({
                            ...prev,
                            [src]: {
                                width: img.naturalWidth,
                                height: img.naturalHeight
                            }
                        }))
                        resolve()
                    }
                    img.onerror = () => resolve()
                    img.src = src
                })
            })

            await Promise.all(preloadPromises)
            setImagesPreloaded(true)
        }

        preloadAllImages()
    }, [images])

    const getRandomColumns = useCallback((count, mobile) => {
        const cols = mobile ? [0, 1] : [0, 1, 2]
        const picked = []
        const max = Math.min(count, cols.length)
        while (picked.length < max) {
            const idx = Math.floor(Math.random() * cols.length)
            const col = cols.splice(idx, 1)[0]
            picked.push(col)
        }
        return picked
    }, [])



    const getOrientation = (img) => {
        const w = img?.asset?.fields?.file?.details?.image?.width
        const h = img?.asset?.fields?.file?.details?.image?.height
        if (!w || !h) return null
        return h > w ? 'portrait' : 'landscape'
    }

    const getImageId = (img) => img?.asset?.sys?.id || img?.path || img?.src || JSON.stringify(img)

    const pickAlternatingForColumns = useCallback((cols) => {
        const sortedCols = [...cols].sort((a, b) => a - b)
        const portraits = images.filter((i) => getOrientation(i) === 'portrait')
        const landscapes = images.filter((i) => getOrientation(i) === 'landscape')

        const makeSeq = (len, start) => Array.from({ length: len }, (_, idx) => (idx % 2 === 0 ? start : (start === 'portrait' ? 'landscape' : 'portrait')))
        const feasible = (seq) => {
            const needP = seq.filter((s) => s === 'portrait').length
            const needL = seq.length - needP
            return needP <= portraits.length && needL <= landscapes.length
        }

        let seqLen = sortedCols.length
        let startType = portraits.length >= landscapes.length ? 'portrait' : 'landscape'
        let seq = makeSeq(seqLen, startType)
        if (!feasible(seq)) {
            startType = startType === 'portrait' ? 'landscape' : 'portrait'
            seq = makeSeq(seqLen, startType)
        }
        while (!feasible(seq) && seqLen > 1) {
            seqLen -= 1
            seq = makeSeq(seqLen, startType)
        }

        const result = []
        const usedPaths = new Set()
        const pickFrom = (arr) => {
            if (!arr.length) return null
            // Filter out both used paths in this selection AND previously shown images
            const available = arr.filter((a) => !usedPaths.has(a.path) && !previousImagesRef.current.has(getImageId(a)))
            // If no unused images, only clear history if we have enough total images
            if (!available.length) {
                // Only clear history if we have more images than currently displayed
                const totalImages = images.length
                const displayedCount = previousImagesRef.current.size
                if (totalImages > displayedCount) {
                    // Clear only the oldest half of the history, keeping recent ones
                    const historyArray = Array.from(previousImagesRef.current)
                    const keepRecent = Math.floor(historyArray.length / 2)
                    previousImagesRef.current.clear()
                    historyArray.slice(-keepRecent).forEach(id => previousImagesRef.current.add(id))
                }

                const fallback = arr.filter((a) => !usedPaths.has(a.path) && !previousImagesRef.current.has(getImageId(a)))
                if (!fallback.length) {
                    // Last resort: pick any unused in current selection
                    const lastResort = arr.filter((a) => !usedPaths.has(a.path))
                    if (!lastResort.length) return null
                    const idx = Math.floor(Math.random() * lastResort.length)
                    const item = lastResort[idx]
                    usedPaths.add(item.path)
                    return item
                }
                const idx = Math.floor(Math.random() * fallback.length)
                const item = fallback[idx]
                usedPaths.add(item.path)
                return item
            }
            const idx = Math.floor(Math.random() * available.length)
            const item = available[idx]
            usedPaths.add(item.path)
            return item
        }

        for (let i = 0; i < seqLen; i++) {
            const need = seq[i]
            const img = need === 'portrait' ? pickFrom(portraits) : pickFrom(landscapes)
            if (img) {
                result.push({ col: sortedCols[i], image: img })
            }
        }

        return result
    }, [images])

    const assignRandom = useCallback((count, delay = 0, preserveSizes = false) => {
        const mobile = isMobile
        const cols = getRandomColumns(count, mobile)

        // Add currently displayed images to exclusion list before picking new ones
        const currentlyDisplayed = columnAssignments
            .filter(a => a.image)
            .map(a => getImageId(a.image))
        currentlyDisplayed.forEach(id => previousImagesRef.current.add(id))

        const alternating = pickAlternatingForColumns(cols)

        // Track newly selected images
        alternating.forEach(x => {
            if (x.image) {
                previousImagesRef.current.add(getImageId(x.image))
            }
        })

        const byCol = new Map(alternating.map((x) => [x.col, x.image]))
        const assignments = cols.map((c) => {
            // Get previous assignment for this column if preserving sizes
            const prevAssignment = preserveSizes ? columnAssignments.find(a => a.col === c) : null
            // On mobile, sometimes span across both columns
            const shouldSpan = prevAssignment ? prevAssignment.span : (mobile && Math.random() > 0.6)
            const width = prevAssignment ? prevAssignment.width : (mobile ? 100 : Math.floor(Math.random() * 51) + 50)
            return {
                col: c,
                image: byCol.get(c),
                width: width, // Preserve width if available, otherwise random
                span: shouldSpan
            }
        })

        // clear any prior stagger timers
        staggerTimeoutsRef.current.forEach((t) => clearTimeout(t))
        staggerTimeoutsRef.current = []

        if (delay > 0 && assignments.length > 1) {
            // reveal assignments one-by-one with `delay` ms gap
            for (let i = 0; i < assignments.length; i++) {
                const t = setTimeout(() => {
                    setColumnAssignments(assignments.slice(0, i + 1))
                }, i * delay)
                staggerTimeoutsRef.current.push(t)
            }
        } else {
            setColumnAssignments(assignments)
        }
    }, [isMobile, getRandomColumns, pickAlternatingForColumns, columnAssignments])

    const assignToColumns = useCallback((cols) => {
        const mobile = isMobile
        const alternating = pickAlternatingForColumns(cols)

        // Track newly selected images to prevent repeats on next selection
        alternating.forEach(x => {
            if (x.image) {
                previousImagesRef.current.add(getImageId(x.image))
            }
        })

        const byCol = new Map(alternating.map((x) => [x.col, x.image]))
        const assignments = cols.map((c) => {
            const shouldSpan = mobile && Math.random() > 0.6
            return {
                col: c,
                image: byCol.get(c),
                width: mobile ? 100 : Math.floor(Math.random() * 51) + 50,
                span: shouldSpan
            }
        })

        setColumnAssignments(assignments)
    }, [isMobile, pickAlternatingForColumns])

    useEffect(() => {
        // Initial: display 1 image on the right column only
        if (images && images.length > 0) {
            const rightCol = isMobile ? 1 : 2
            assignToColumns([rightCol])
            setFirstImageVisible(false)
            const timer = setTimeout(() => setFirstImageVisible(true), 250)
            return () => clearTimeout(timer)
        }
    }, [images, isMobile, assignToColumns])

    const handleClick = () => {
        // Show 2 or 3 random images in random columns (or 1-2 on mobile)
        const count = isMobile ? (Math.random() > 0.5 ? 2 : 1) : (Math.random() > 0.5 ? 3 : 2)
        // Preserve sizes to create consistent feeling that pictures are always random/different
        assignRandom(Math.min(count, images.length), 50, true)
    }

    // Shuffle images on mouse wheel scroll (one change per gesture)
    useEffect(() => {
        let fired = false
        let resetTimer = null

        const handleWheel = (e) => {
            if (Math.abs(e.deltaY) < 5) return

            // Reset the "gesture ended" timer on every incoming event
            clearTimeout(resetTimer)
            resetTimer = setTimeout(() => { fired = false }, 600)

            // Only fire once per gesture
            if (fired) return
            fired = true

            const count = isMobile ? (Math.random() > 0.5 ? 2 : 1) : (Math.random() > 0.5 ? 3 : 2)
            assignRandom(Math.min(count, images.length), 0, true)
        }

        window.addEventListener('wheel', handleWheel, { passive: true })
        return () => {
            window.removeEventListener('wheel', handleWheel)
            clearTimeout(resetTimer)
        }
    }, [isMobile, images, assignRandom])

    const renderColumnContent = (colIndex) => {
        const assignment = columnAssignments.find((a) => a.col === colIndex)
        if (!assignment || !assignment.image) return null
        const { image, width, span } = assignment
        const optimizedSrc = getDprImageUrl(image.asset || image.src, 750)
        // Apply fade animation to all images
        const assignmentIndex = columnAssignments.indexOf(assignment)
        return (
            <div
                className={`imageWrapper ${span ? 'span-full' : ''}`}
                style={{ width: span ? '100%' : `${width}%` }}
            >
                <img
                    src={optimizedSrc}
                    alt={image.caption || 'Artwork'}
                    className={`fade-in-motion`}
                    style={{ animationDelay: `${assignmentIndex * 0}s` }}
                    onClick={handleClick}
                    onMouseEnter={() => setHoveredItem(image)}
                    onMouseLeave={() => setHoveredItem(null)}
                    {...(imageDimensions[optimizedSrc]?.width && { width: imageDimensions[optimizedSrc].width })}
                    {...(imageDimensions[optimizedSrc]?.height && { height: imageDimensions[optimizedSrc].height })}
                />
            </div>
        )
    }

    const numColumns = isMobile ? 2 : 3

    return (
        <div className='homePage'>
            <Helmet>
                <title>Marguerite Soulier</title>
                <link rel="canonical" href="https://margueritesoulier.fr/" />
                <meta name="description" content="Marguerite Soulier est artiste, designer textile et bergère saisonnière basée dans le Béarn." />
                <meta property="og:title" content="Marguerite Soulier" />
                <meta property="og:url" content="https://margueritesoulier.fr/" />
                <meta property="og:description" content="Marguerite Soulier est artiste, designer textile et bergère saisonnière basée dans le Béarn." />
            </Helmet>

            <div className='gridContainer'>
                {Array.from({ length: numColumns }).map((_, col) => (
                    <div key={col} className='gridColumn'>
                        {renderColumnContent(col)}
                    </div>
                ))}
            </div>
            <div className={`homeCaption ${hoveredItem && !isMobile ? 'visible' : ''}`}>
                {hoveredItem?.caption && (
                    hoveredItem.caption
                        .split(' — ')
                        .filter(Boolean)
                        .slice(0, -1)
                        .map((part, idx) => (
                            <span key={idx}>{part}</span>
                        ))
                )}
                {hoveredItem && (
                    <span className={`stockDot ${hoveredItem.stock ? 'inStock' : 'outStock'}`} />
                )}
            </div>
        </div>
    )
}

export default Home